import React, { useEffect, useRef, useState } from "react";
import ReactCountryFlag from "react-country-flag";
import styled from "styled-components";
import { allCountrycodes, countries, Country, countryCodes } from "@domain/currentProfile/countryCode";
import { useOutsideAlerter } from "@utils/hooks";
import { normalizedString } from "@utils/strings";
import { Row } from "@ui/components/layout";
import { SimpleTextInput } from "./simpleTextInput";

interface CountryDatalistProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "onChange"> {
	id: string;
	value: Country | undefined;
	onChange: (v: Country) => void;
	placeholder?: string;
	options: typeof allCountrycodes;
	disabled?: boolean;
}
export const CountryDatalist: React.FC<CountryDatalistProps> = ({
	id,
	value,
	onChange,
	placeholder,
	options,
	disabled,
	...htmlProps
}) => {
	const flushedCodes = options.map((country) => countries[country]);
	const inputRef = useRef<HTMLDivElement>(null);
	const [finalOptions, setFinalOptions] = useState(
		flushedCodes.filter((opt) => opt.toLowerCase().includes(normalizedString(value ?? "").toLowerCase()))
	);
	useOutsideAlerter(inputRef, () => setOpened(false));
	const [opened, setOpened] = useState<boolean>(false);
	const [choosed, setChoosed] = useState<boolean>(false);

	useEffect(() => {
		setOpened(true);
		setFinalOptions(
			flushedCodes.filter((opt) => opt.toLowerCase().includes(normalizedString(value ?? "").toLowerCase()))
		);
	}, [value]);

	useEffect(() => {
		if (choosed) {
			setOpened(false);
			setChoosed(false);
		}
	}, [choosed]);

	useEffect(() => {
		setOpened(false);
	}, []);

	return (
		<Container ref={inputRef} {...htmlProps}>
			<SimpleTextInput
				placeholder={placeholder}
				value={value ?? ""}
				list={id}
				onChange={(v) => {
					onChange(v as Country);
				}}
				iconPosition="left"
				icon={
					value ? (
						<ReactCountryFlag
							className="emojiFlag"
							countryCode={countryCodes[value]}
							style={{
								fontSize: 20,
								marginRight: 8,
							}}
							aria-label={value}
						/>
					) : undefined
				}
				isApplication
			/>
			{opened && finalOptions && finalOptions.length > 0 && (
				<OptionsContainer width={inputRef?.current?.clientWidth ? inputRef?.current?.clientWidth - 5 : undefined}>
					{finalOptions.map((opt, index) => (
						<StyledOption
							key={index}
							onClick={() => {
								onChange(opt);
								setOpened(false);
								setChoosed(true);
							}}
						>
							<ReactCountryFlag
								className="emojiFlag"
								countryCode={countryCodes[opt]}
								style={{
									fontSize: 20,
								}}
								aria-label={opt}
							/>
							<span style={{ marginLeft: 14 }}>{opt}</span>
						</StyledOption>
					))}
				</OptionsContainer>
			)}
		</Container>
	);
};

const Container = styled.div``;

const OptionsContainer = styled.div.withConfig({
	shouldForwardProp: (prop) => !["isApplication", "height"].includes(prop),
})<{ width?: string | number; height?: string | number; isApplication?: boolean }>`
	min-width: ${({ width }) => (width ? width : 200)}px;
	max-width: ${({ width }) => (width ? width : 200)}px;
	height: fit-content;
	max-height: ${({ height }) => (height ? height : 240)}px;
	overflow: auto;
	position: absolute;
	z-index: 10;
	border: #b7afcc 1px solid;

	background-color: white;
	height: -webkit-fit-content;
	height: -moz-fit-content;
	height: fit-content;
	margin: 5px;
	margin-top: 0;
	margin-left: 0;
	border-radius: 2px;
	::-webkit-scrollbar {
		width: 8px;
		height: 4px;
	}

	/* Track */
	::-webkit-scrollbar-track {
		box-shadow: inset 0 0 5px grey;
		border-radius: 10px;
	}

	/* Handle */
	::-webkit-scrollbar-thumb {
		background: #007aff;
		border-radius: 10px;
	}

	/* Handle on hover */
	::-webkit-scrollbar-thumb:hover {
		background: #b30000;
	}
`;

const StyledOption = styled(Row)`
	width: 100%;
	padding: 10px;
	border-top: #d2d2d2 0.5px solid;
	font-family: Sora;
	font-size: 15px;
	cursor: pointer;
`;
